import { Suspense, useEffect, useMemo, useRef } from 'react';
import { Outlet } from 'react-router-dom';

import PartnerScoreFilter from '@/pages/PartnerScore/components/PartnerScoreFilter/PartnerScoreFilter';
import {
  usePartnerScoreFilter,
  UsePartnerScoreFilterReturn,
  PartnerScoreFiltersStorage,
} from '@/pages/PartnerScore/components/PartnerScoreFilter';

import styles from './PartnerScoreLayout.module.scss';
import { LoadingSpinnerFallback } from '@/storybook/components/LoadingSpinner/LoadingSpinner';
import PartnerScoreNavigation from '@/components/base/PartnerScoreLayout/PartnerScoreNavigation';
import { useTranslation } from 'react-i18next';
import PageBanner from '../Layout/PageBanner/PageBanner';
import { useLocalFiltersRegistry } from '@/context/LocalFiltersRegistry/LocalFiltersRegistry';
import { LOCAL_FILTERS_KEY_PARTNER_SCORE } from '@/context/LocalFiltersRegistry/localFiltersKeys';
import { SessionStorageKey, syncSessionStorage, clearSessionStorage } from '@/hooks/useSessionStorage';
import { isEqual } from 'lodash-es';

type ExcludedUsePartnerScoreFilterReturn = Pick<UsePartnerScoreFilterReturn, 'year' | 'quarter' | 'surveyIdList'>;

export const testIds = {
  tabSwitcherContainer: 'ces-tab-switcher-container',
  tabSwitcherLink: (label: string) => `ces-tab-switcher-link-${label}`,
  tabSwitcherLabel: (label: string) => `ces-tab-switcher-label-${label}`,
  tabSwitcherCoverage: (label: string) => `ces-tab-switcher-coverage-${label}`,
  tabSwitcherValue: (label: string) => `ces-tab-switcher-value-${label}`,
  tabSwitcherArrowDirection: (label: string) => `ces-tab-switcher-arrow-direction-${label}`,
  tabSwitcherCaption: (label: string) => `ces-tab-switcher-caption-${label}`,
};

export interface PartnerScoreLayoutReturn extends ExcludedUsePartnerScoreFilterReturn {}

export default function PartnerScoreLayout() {
  const { t } = useTranslation();

  const filterHook = usePartnerScoreFilter();
  const { year, quarter, surveyIdList, yearQuarterValue, yearQuarterOptions, setFilters, defaultFilters } = filterHook;

  const { register, unregister } = useLocalFiltersRegistry();
  const filtersRef = useRef<PartnerScoreFiltersStorage>({ yearQuarter: yearQuarterValue, surveyTitle: surveyIdList });
  filtersRef.current = { yearQuarter: yearQuarterValue, surveyTitle: surveyIdList };

  const defaultFiltersRef = useRef(defaultFilters);
  defaultFiltersRef.current = defaultFilters;

  useEffect(() => {
    register(LOCAL_FILTERS_KEY_PARTNER_SCORE, {
      getFilters: () => filtersRef.current,
      setFilters: (incoming: Record<string, unknown>) => {
        const merged: PartnerScoreFiltersStorage = {
          yearQuarter: Array.isArray(incoming.yearQuarter) ? incoming.yearQuarter : [],
          surveyTitle: Array.isArray(incoming.surveyTitle) ? incoming.surveyTitle : [],
        };
        syncSessionStorage(SessionStorageKey.PARTNER_SCORE_FILTERS, merged);
      },
      getDefaultFilters: () => defaultFiltersRef.current,
    });
    return () => {
      unregister(LOCAL_FILTERS_KEY_PARTNER_SCORE);
    };
  }, [register, surveyIdList, unregister, yearQuarterValue]);

  useEffect(() => {
    return () => {
      if (isEqual(filtersRef.current, defaultFiltersRef.current)) {
        clearSessionStorage(SessionStorageKey.PARTNER_SCORE_FILTERS);
      }
    };
  }, []);

  const storedFilters = useMemo<PartnerScoreFiltersStorage>(
    () => ({ yearQuarter: yearQuarterValue, surveyTitle: surveyIdList }),
    [yearQuarterValue, surveyIdList],
  );

  const outletContextProps = useMemo(
    () => ({
      year,
      quarter,
      surveyIdList,
    }),
    [quarter, year, surveyIdList],
  );

  return (
    <div className={styles.container}>
      <PageBanner.Title>{t('header.navigation.partnerScore')}</PageBanner.Title>
      <section className={styles.section}>
        <article className={styles.sectionItem}>
          <PartnerScoreFilter
            storedFilters={storedFilters}
            yearQuarterOptions={yearQuarterOptions}
            onFiltersChange={setFilters}
          />
        </article>
      </section>
      <PartnerScoreNavigation year={year} quarter={quarter} surveyIdList={surveyIdList}>
        <Suspense fallback={<LoadingSpinnerFallback />}>
          <Outlet context={outletContextProps} />
        </Suspense>
      </PartnerScoreNavigation>
    </div>
  );
}
